import { CarerRole } from '@lmc/ts-common'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { NAMESPACE } from 'locale'
import { useQuery } from 'react-query'
import { getJSON } from 'common/services/api'

export enum NeedsAndSkills {
  skills = 'skills',
  needs = 'needs',
}

type SkillType = {
  type: NeedsAndSkills.skills
  elements?: { skillName?: string }[]
}
type NeedType = {
  type: NeedsAndSkills.needs
  elements?: { needName?: string }[]
}

type ElementsType = SkillType | NeedType

const getContactTranslationString = (type: NeedsAndSkills) => ({
  [CarerRole.careHomeAdmin]: `${type}ContactCarehomeAdmin`,
  [CarerRole.groupAdmin]: `${type}ContactGroupAdmin`,
})

const useSkillsAndNeedsTemplate = <T extends ElementsType>(props: T) => {
  const { data } = useQuery<{ result?: { id: string }[] }>(
    'skill-need-templates',
    () => getJSON({ url: '/api/list/skill-need-templates' })
  )

  const hasTemplates = data?.result && data?.result?.length > 0

  const { type, elements } = props

  const { user, home } = useSelector((state: any) => state.global)
  const { role } = user
  const { homeGroup } = home

  const isHomeGroup = !!homeGroup
  const { t } = useTranslation(NAMESPACE.RESOURCES)

  const roleWhoCanEditSkillsAndNeedsTemplate = isHomeGroup
    ? CarerRole.groupAdmin
    : CarerRole.careHomeAdmin

  const hasPermisionToAddSkillsOrNeedsTemplate =
    role === roleWhoCanEditSkillsAndNeedsTemplate

  const hasElements = elements && elements?.length > 0

  const link = () => {
    if (hasTemplates) {
      return undefined
    }

    if (hasPermisionToAddSkillsOrNeedsTemplate) {
      return (
        <Link to="/settings/skills-and-needs">
          {t(`skillsAndNeeds.addViaTemplate`)}
        </Link>
      )
    }

    return undefined
  }

  const renderText = () => {
    if (hasElements) {
      if (type === 'needs') {
        return elements?.length
          ? elements?.map((need) => need.needName).join(', ')
          : null
      }

      if (type === 'skills') {
        return elements?.length
          ? elements?.map((skills) => skills.skillName).join(', ')
          : null
      }
    }

    if (hasTemplates) {
      return t(`skillsAndNeeds.${type}NotAddedYet`)
    }

    if (hasPermisionToAddSkillsOrNeedsTemplate) {
      return t(`skillsAndNeeds.${type}NotAddedToTheTemplate`)
    }

    return t(
      `skillsAndNeeds.${
        getContactTranslationString(type)[roleWhoCanEditSkillsAndNeedsTemplate]
      }`
    )
  }

  return {
    linkNode: link(),
    textNode: renderText(),
    hasElements,
    hasTemplates,
  }
}

export default useSkillsAndNeedsTemplate
